import {
    LoadingIndicator,
    ProjectFeatureToggleFlags,
    useProjectHasFeatureToggle,
} from '@ygtechnologies/bygr-frontend-component-library';
import BuyerRouting from '@/buyer/BuyerRouting';
import AuthRoute, { AuthRouteConditions } from '@/components/app/AuthRoute';
import NotFound from '@/components/error-pages/NotFound';
import HeaderContext from '@/components/layout/Header/HeaderContext';
import React, { useContext, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RouteComponentProps, useParams, withRouter } from 'react-router';
import { Redirect, Route, Switch } from 'react-router-dom';
import BuyerMassInvite from '@/seller/components/residential-units/BuyerMassInvite/BuyerMassInvite';
import ResidentialUnitDetails from '@/seller/components/residential-units/ResidentialUnitDetails/ResidentialUnitDetails';
import ResidentialUnitImport from '@/seller/components/residential-units/ResidentialUnitImport';
import { setActiveProjectId } from '@/state/global/creators';
import { IState } from '@/state/IState';
import { useAppDispatch, useIsMobile } from '@/utils/hooks';
import ChangeRequestsPage, { ChangeRequestsPageTab } from '../change-requests/ChangeRequestsPage';
import CommentsPage from '../comments/CommentsPage';
import ProjectCommentThreadMobile from '../comments/ProjectCommentThreadMobile';
import ResidentialUnitCommentThreadMobile from '../comments/ResidentialUnitCommentThreadMobile';
import DocumentsPage from '../documents/DocumentsPage';
import ExternalAgreementsPage from '../external-agreements/ExternalAgreementsPage';
import { Meeting } from '../Meetings/ExternalMeetings/Meeting';
import { Meetings } from '../Meetings/Meetings';
import OrderDetailsPage from '../orders/OrderDetailsPage';
import OrdersPage from '../orders/OrdersPage';
import ProjectCatalog from '../project-catalog/ProjectCatalog';
import LandingPageSetup from './LandingPageSetup/LandingPageSetup';
import ProjectDetails from './ProjectDetails';
import ProjectInviteExternal from './ProjectInvite/ProjectInviteExternal';
import ProjectInviteInternal from './ProjectInvite/ProjectInviteInternal';
import ProjectResidentialUnits, { ResidentialUnitsTab } from './ProjectResidentialUnits/ProjectResidentialUnits';
import { ProjectRooms } from './ProjectRooms';
import { OptionGroupsCreatePage } from '../options/OptionGroupsCreatePage';
import { OptionsPage } from '../options/OptionsPage';

const manageUnitsOnly: AuthRouteConditions = { project: 'canManageResidentialUnits' };
const inviteBuyersOnly: AuthRouteConditions = { project: 'canInviteBuyers' };
// const viewDashboardOnly: AuthRouteConditions = { project: 'canViewDashboard' };
const editLandingPageOnly: AuthRouteConditions = { project: 'canEditLandingPage' };
const adminOnly: AuthRouteConditions = { project: 'isAdministrator' };

type OwnProps = RouteComponentProps<any> & {};

const ProjectRouting: React.FC<OwnProps> = ({ match }) => {
    const { projectId } = useParams<{ projectId: string }>();
    const activeProject = useSelector((state: IState) => state.global.activeProject);
    const dispatch = useAppDispatch();
    const dependencyIsEnabled = useProjectHasFeatureToggle(ProjectFeatureToggleFlags.DependenciesInOptions);

    const isMobile = useIsMobile();

    const headerContext = useContext(HeaderContext);
    useEffect(() => {
        headerContext.isInProject = true;

        return () => {
            headerContext.isInProject = false;
        };
    }, []);

    useEffect(() => {
        if (activeProject?.id !== +projectId) {
            setActiveProjectId(dispatch, +projectId);
        }
    }, [activeProject, projectId, dispatch]);

    const baseUrl = match.path;

    //Fixes redirect after changing profile
    //TODO: find better solution instead of this workaround
    if (!activeProject || activeProject?.id !== +projectId) {
        return <LoadingIndicator />;
    }

    return (
        <Switch>
            {activeProject.permissions.canViewDashboard && (
                <Redirect exact path={`${baseUrl}`} to={`${baseUrl}/dashboard`} />
            )}

            {!activeProject.permissions.canViewDashboard && (
                <Redirect exact path={`${baseUrl}`} to={`${baseUrl}/residential-units`} />
            )}

            <AuthRoute exact path={`${baseUrl}/:tab(dashboard)`} component={ProjectDetails} />
            <AuthRoute
                exact
                path={`${baseUrl}/:tab(landing-page)`}
                component={ProjectDetails}
                conditions={editLandingPageOnly}
            />
            <AuthRoute exact path={`${baseUrl}/:tab(settings)/:settingsTab?`} component={ProjectDetails} />
            <Redirect exact from={`${baseUrl}/white-label`} to={`${baseUrl}/settings/white-label`} />
            <AuthRoute exact path={`${baseUrl}/:tab(users)`} component={ProjectDetails} conditions={adminOnly} />
            <AuthRoute exact path={`${baseUrl}/:tab(project-thread)`} component={ProjectDetails} />
            <AuthRoute exact path={`${baseUrl}/:tab(history-log)`} component={ProjectDetails} conditions={adminOnly} />
            <AuthRoute exact path={`${baseUrl}/:tab(integrations)`} component={ProjectDetails} conditions={adminOnly} />
            <AuthRoute exact path={`${baseUrl}/:tab(survey)`} component={ProjectDetails} conditions={adminOnly} />
            <AuthRoute exact path={`${baseUrl}/users/invite`} component={ProjectInviteExternal} />
            <AuthRoute exact path={`${baseUrl}/users/add`} component={ProjectInviteInternal} />
            <AuthRoute exact path={`${baseUrl}/landing-page`} component={LandingPageSetup} />

            {/* <AuthRoute exact path={`${baseUrl}/orders`} component={ProjectOrders} /> */}
            {/* <AuthRoute exact path={`${baseUrl}/documents`} component={ProjectDocuments} /> */}

            <AuthRoute
                exact
                path={`${baseUrl}/residential-units/import`}
                component={ResidentialUnitImport}
                conditions={manageUnitsOnly}
            />

            <AuthRoute exact path={`${baseUrl}/invite-buyers`} component={BuyerMassInvite} conditions={inviteBuyersOnly} />

            {/* Catalog */}
            <AuthRoute exact path={`${baseUrl}/catalog/:tab(products)/:id?`} component={ProjectCatalog} />
            <Redirect exact path={`${baseUrl}/catalog`} to={`${baseUrl}/catalog/products`} />

            {/* Residential units */}
            <Redirect exact path={`${baseUrl}/residential-units`} to={`${baseUrl}/residential-units/compressed`} />
            <Redirect
                exact
                path={`${baseUrl}/residential-units/by-last-message`}
                to={`${baseUrl}/residential-units/by-last-message/compressed`}
            />
            <AuthRoute
                exact
                path={`${baseUrl}/residential-units/:viewMode(compressed|expanded)?`}
                component={ProjectResidentialUnits}
                extraProps={{ tab: ResidentialUnitsTab.All }}
            />

            <AuthRoute
                exact
                path={`${baseUrl}/residential-units/by-last-message/:viewMode(compressed|expanded)?`}
                component={ProjectResidentialUnits}
                extraProps={{ tab: ResidentialUnitsTab.ByLastMessage }}
            />

            <AuthRoute
                exact
                path={`${baseUrl}/residential-units/messages/:id?`}
                component={ProjectResidentialUnits}
                extraProps={{ tab: ResidentialUnitsTab.Messages }}
            />
            <AuthRoute
                exact
                path={`${baseUrl}/residential-units/rooms/`}
                component={ProjectResidentialUnits}
                extraProps={{ tab: ResidentialUnitsTab.Rooms }}
            />

            {/* <AuthRoute
                exact
                path={`${baseUrl}/residential-units/:viewMode(compressed|expanded)`}
                component={ProjectResidentialUnits}
            /> */}

            <AuthRoute
                path={`${baseUrl}/residential-units/:residentialUnitId/preview`}
                component={BuyerRouting}
                conditions={manageUnitsOnly}
            />

            <Redirect
                exact
                path={`${baseUrl}/residential-units/:residentialUnitId`}
                to={`${baseUrl}/residential-units/:residentialUnitId/dashboard`}
            />

            {isMobile && (
                <AuthRoute
                    exact
                    path={`${baseUrl}/residential-units/:residentialUnitId/:tab(comments)/:id`}
                    component={ResidentialUnitCommentThreadMobile}
                />
            )}

            <AuthRoute
                path={`${baseUrl}/residential-units/:residentialUnitId/:tab(dashboard|documents|external-agreements|changes|orders|options|comments|floor-plan|history-log)/:id?`}
                component={ResidentialUnitDetails}
            />

            {/* Messages */}
            {isMobile && <AuthRoute exact path={`${baseUrl}/messages/:id`} component={ProjectCommentThreadMobile} />}
            <AuthRoute exact path={`${baseUrl}/messages/:id?`} component={CommentsPage} />

            {/* <AuthRoute exact path={`${baseUrl}/residential-unit-groups/new`} component={ResidentialUnitGroupEdit} />
            <AuthRoute
                exact
                path={`${baseUrl}/residential-unit-groups/:residentialUnitGroupId/settings`}
                component={ResidentialUnitGroupEdit}
            /> */}

            {/* Orders */}
            <AuthRoute exact path={`${baseUrl}/orders/:mode(change-requests|options)?/:id?`} component={OrdersPage} />
            <AuthRoute exact path={`${baseUrl}/orders/:orderId`} component={OrderDetailsPage} />

            <AuthRoute exact path={`${baseUrl}/meetings`} component={Meetings} />
            <AuthRoute
                exact
                path={`${baseUrl}/meetings/:tab(internal|external|meetingtypes|overview)/:settingsTab?`}
                component={Meetings}
            />
            <AuthRoute exact path={`${baseUrl}/meetings/:meetingId`} component={Meeting} />

            {/* External agreements */}
            <Redirect
                exact
                path={`${baseUrl}/external-agreements/tasks`}
                to={`${baseUrl}/external-agreements/tasks/pending`}
            />
            <AuthRoute
                exact
                path={`${baseUrl}/external-agreements/tasks/:status(rejected|pending)?/:id?`}
                component={ExternalAgreementsPage}
                extraProps={{ tabId: 'tasks' }}
            />
            <AuthRoute
                exact
                path={`${baseUrl}/external-agreements/messages/:status(approved|rejected|pending|canceled)?/:id?`}
                component={ExternalAgreementsPage}
                extraProps={{ tabId: 'messages' }}
            />
            <AuthRoute
                exact
                path={`${baseUrl}/external-agreements/:status(approved|rejected|pending|canceled)?/:id?`}
                component={ExternalAgreementsPage}
                extraProps={{ tabId: 'main' }}
            />

            {/* Documents */}
            <AuthRoute exact path={`${baseUrl}/:tab(documents|project-documents)`} component={DocumentsPage} />

            {/* Changes - do not reorder unless there is a good reason */}
            <Redirect exact path={`${baseUrl}/changes/overview`} to={`${baseUrl}/changes/overview/unit`} />
            <AuthRoute
                exact
                path={`${baseUrl}/changes/overview/:mode`}
                component={ChangeRequestsPage}
                extraProps={{ tab: ChangeRequestsPageTab.Overview }}
            />

            <Redirect exact path={`${baseUrl}/changes/tasks`} to={`${baseUrl}/changes/tasks/unassigned`} />
            <AuthRoute
                exact
                path={`${baseUrl}/changes/tasks/:mode`}
                component={ChangeRequestsPage}
                extraProps={{ tab: ChangeRequestsPageTab.Tasks }}
            />

            <AuthRoute
                exact
                path={`${baseUrl}/changes/:tab(${Object.values(ChangeRequestsPageTab).join('|')})?/:id?`}
                component={ChangeRequestsPage}
            />

            {/* <AuthRoute exact path={`${baseUrl}/dialogs`} component={ProjectDialogs} /> */}
            {/* <AuthRoute exact path={`${baseUrl}/dialogs/:id?`} component={ProjectDialogs} /> */}

            <AuthRoute exact path={`${baseUrl}/options/new`} component={OptionGroupsCreatePage} />
            <Redirect exact path={`${baseUrl}/options`} to={`${baseUrl}/options/options`} />
            <AuthRoute exact path={`${baseUrl}/options/:sourceOptionGroupId/copy`} component={OptionGroupsCreatePage} />
            <AuthRoute path={`${baseUrl}/options/:tabId?`} component={OptionsPage} />

            <Route component={NotFound} />
        </Switch>
    );
};

export default withRouter(ProjectRouting);
